import { Loader } from '@sgme/ui';
import classNames from 'classnames';
import type { IntlKey } from 'locales';
import { FormattedMessage } from 'react-intl';
import type { ColorLevel } from 'types/sgBootstrap';
import type { TypedError } from 'utils/errors/errors';
import type { LoadableContent } from 'utils/status';

type Props = {
  onConfirm: () => void;
  onDiscard: () => void;

  onClose: () => void;
  colorLevel: ColorLevel;
  confirmMessageLabel: IntlKey;
  successMessageLabel?: IntlKey;
  errorLabel: IntlKey;
} & LoadableContent<unknown, TypedError>;

export function Banner({
  onConfirm,
  onDiscard,
  colorLevel,
  confirmMessageLabel,
  successMessageLabel = 'scheduledRun.schedule.updated',
  errorLabel,
  onClose,
  ...props
}: Props) {
  if (props.status === 'error') {
    colorLevel = 'warning';
  }
  return (
    <div
      className={classNames(
        { [`bg-${colorLevel}`]: props.status !== 'success', 'flex-nowrap': props.status === 'success' },
        `d-flex  flex-fill  flex-wrap px-3 py-4 align-items-center`,
      )}
    >
      <div className="d-flex flex-fill justify-content-between">
        <div className="d-flex align-items-center">
          <em className="icon icon-outlined me-2">
            {props.status === 'error' && 'warning'}
            {props.status === 'success' && 'check_circle'}
            {props.status === 'idle' && 'info'}
          </em>
          {props.status === 'loading' && <FormattedMessage id="scheduledRun.schedule.updating" />}
          {props.status === 'success' && <FormattedMessage id={successMessageLabel} />}
          {props.status === 'idle' && <FormattedMessage id={confirmMessageLabel} />}
          {props.status === 'error' && <FormattedMessage id={errorLabel} />}
          {props.status === 'error' && props.error?.type === 'technical' && props.error.subType === 'timeout' && (
            <FormattedMessage id="scheduledRun.update.timeout.error" />
          )}
        </div>
        <div>
          {props.status === 'idle' && (
            <div>
              <button className={`btn btn-${colorLevel} bg-${colorLevel} btn-lg`} onClick={onConfirm}>
                <FormattedMessage id="yes" />
              </button>
              <button className={`btn btn-${colorLevel} bg-${colorLevel} btn-lg`} onClick={onDiscard}>
                <FormattedMessage id="no" />
              </button>
            </div>
          )}
          {props.status === 'loading' && (
            <div className="ms-2">
              <Loader purpose="spinner" size="sm" />
            </div>
          )}
          {(props.status === 'success' || props.status === 'error') && (
            <button
              className={classNames('btn btn-icon', {
                'text-alt': props.status === 'error',
                'text-primary': props.status === 'success',
              })}
              onClick={onClose}
            >
              <em className="icon">close</em>
            </button>
          )}
        </div>
      </div>

      {props.status === 'error' && props.error?.type === 'functional' && (
        <div className="d-flex mt-1 w-100 overflow-y-auto" style={{ maxHeight: 200 }}>
          {props.error.message}
        </div>
      )}
      {props.status === 'error' && props.error?.type === 'technical' && props.error.subType === 'timeout' && (
        <div className="d-flex mt-1 w-100 overflow-y-auto">
          <FormattedMessage id="generic.timeout.message" />
        </div>
      )}
    </div>
  );
}
